import * as jotai from 'jotai'

import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'

import { useInvalidateCache } from '../utils'

import * as base from './api'

export const PROJECT_NEWS_CACHE = jotai.atom<hooks.Cache<api.NewsListPageWithFacets>>({})

export const defaultInternalProjectNewsParams = {
  limit: 10,
  with_facets: true,
} as const

export const defaultPublicProjectNewsParams = {
  sort: 'created_at_desc',
} as const

export type UseProjectNewsParams = {
  extra_fields?: string
  content_type?: 'image' | 'video' | 'text' | 'audio'
  limit?: number
  page?: number
  public?: boolean
  q?: string
  reverse?: boolean
  reward_id?: string
  sort?: string
  status?: string
  with_facets?: boolean
}

export type UseProjectNewsConfig = {
  fetchOnMount?: boolean | 'force'
  params?: UseProjectNewsParams
  withToken?: boolean
}

export type UseProjectNews = {
  query: () => Promise<void>
  response: hooks.Response<api.NewsListPageWithFacets>
}

export function useProjectNews(
  projectSlugOrId?: number | string,
  { fetchOnMount = true, params = {}, withToken = false }: UseProjectNewsConfig = {},
): api.WithNewsFacets<UseProjectNews> {
  const sharedQuery = hooks.useSharedQuery(
    async (projectId: number | string, params: UseProjectNewsParams, withToken: boolean) =>
      base.getPage(projectId, toURLSearchParams(params), withToken),
    {
      atom: PROJECT_NEWS_CACHE,
      id,
    },
  )

  hooks.useFetchOnMount({ fetchOnMount, query, getResponse }, id(projectSlugOrId, params, withToken))

  return {
    facets: getResponse().data?.facets,
    query,
    response: getResponse(),
  }

  function getResponse(): hooks.Response<api.NewsListPageWithFacets> {
    if (projectSlugOrId === undefined) {
      return { status: hooks.QueryStatus.PRISTINE }
    }
    return sharedQuery.getCurrentResponse([projectSlugOrId, params, withToken])
  }

  async function query(): Promise<void> {
    if (projectSlugOrId === undefined) {
      return
    }

    return sharedQuery.query(projectSlugOrId, params, withToken)
  }
}

export function toURLSearchParams({
  limit,
  page,
  sort,
  status,
  reward_id,
  content_type,
  reverse,
  with_facets,
  public: is_public,
  extra_fields,
}: UseProjectNewsParams): URLSearchParams {
  const searchParams = new URLSearchParams()
  if (limit !== undefined) {
    searchParams.set('limit', String(limit))
  }
  if (page !== undefined) {
    searchParams.set('offset', String((page - 1) * (limit ?? 20)))
  }
  if (status !== undefined) {
    searchParams.set('status', String(status))
  }
  if (sort !== undefined) {
    searchParams.set('sort', String(sort))
  }
  if (reward_id !== undefined) {
    searchParams.set('reward_id', String(reward_id))
  }
  if (content_type !== undefined) {
    searchParams.set('content_type', String(content_type))
  }
  if (reverse !== undefined) {
    searchParams.set('reverse', String(reverse))
  }
  if (with_facets !== undefined) {
    searchParams.set('with_facets', 'true')
  }
  if (is_public !== undefined) {
    searchParams.set('public', String(is_public))
  }
  if (extra_fields !== undefined) {
    searchParams.set('extra_fields', String(extra_fields))
  }

  return searchParams
}

export function id(projectId: number | string | undefined, params: UseProjectNewsParams, withToken: boolean): string {
  return `${String(projectId)}:${JSON.stringify([
    params.limit,
    params.page,
    params.status,
    params.sort,
    params.q,
    params.reward_id,
    params.content_type,
    params.reverse,
    params.public,
    withToken,
  ])}`
}

export function useInvalidateProjectNewsCache(projectSlugOrId: string | number | undefined) {
  const setProjectNewsCache = jotai.useSetAtom(PROJECT_NEWS_CACHE)

  return useInvalidateCache(
    (key) => (projectSlugOrId === undefined ? false : key.startsWith(String(projectSlugOrId))),
    setProjectNewsCache,
  )
}
